






































import { Component, Vue } from 'vue-property-decorator';
import { Product } from '@/API';
import { fetchSearchResult } from '@/appsync/lambda/SearchResultQueryService';
import { isInHouseIP } from '@/appsync/service/MenuQueryService';
import { updateFavoriteStatus } from '@/appsync/service/MutateService';
import { fetchFavoriteMenuIdList } from '@/appsync/service/PortalFavoriteQueryService';
import { updateAccessedTimeStamp } from '@/appsync/service/PortalHistoryMenuMutationService';
import { getIsPortalEnabledStatus } from '@/appsync/service/SystemSettingQueryService';
import SearchInput from '@/components/atoms/SearchInput.vue';
import { getUrlParam } from '@/containers/globals/Util';
import { getUrlTarget } from '@/containers/globals/Util';
import { refreshStores } from '@/containers/globals/globalSharedService';
import PageNotFound from '@/containers/pages/PageNotFound.vue';
import SearchResultBody from '@/containers/templates/SearchResultBody.vue';
import { i18n } from '@/plugins/i18n/i18n';
import router from '@/router';
import { commonModule } from '@/store/modules/CommonStore';
import { favoriteListModule } from '@/store/modules/FavoriteListStore';
import { loginUserModule } from '@/store/modules/LoginUserStore';
import { searchModule } from '@/store/modules/SearchStore';
import {
  ID,
  ProductMenu,
  LoginInfo,
  TenantLandscape,
  UserInfo,
  UpdateTimeStampQueryParam,
} from '@/types/type';
import {
  fixedEncodeURIComponent,
  _BooleanEnum2boolean,
} from '@/utils/CommonUtils';
import { REL } from '@/utils/consts';

interface SearchResult {
  resultList: ProductMenu[];
  time: number;
}

@Component({
  components: {
    SearchInput,
    SearchResultBody,
    PageNotFound,
  },
})
export default class Search extends Vue {
  private queryText: string = '';
  private screenLoading: boolean = true;
  private searchLoading: boolean = false;
  private isPortalEnabled: boolean = true;

  private get literals() {
    return {
      placeholder: this.screenLoading ? '' : i18n.tc('appbar.placeholder'),
      back: this.screenLoading ? '' : i18n.tc('common.back'),
    };
  }

  public async created(): Promise<void> {
    const query: string | null = this.$route.params.query as string | null;
    this.queryText = query ? query : '';
    const loginInfo: LoginInfo = commonModule.loginInfo;

    this.isPortalEnabled = await getIsPortalEnabledStatus(
      loginInfo.tenant,
      loginInfo.landscape
    );
  }

  public async mounted(): Promise<void> {
    (this.$refs.searchInput as InstanceType<typeof SearchInput>).focus();
    await refreshStores(commonModule.loginInfo);
    this.screenLoading = false;
    if (this.existQueryText) {
      this.searchLoading = true;
      const result = await this.fetchResult(this.queryText);
      searchModule.setResults(result.resultList);
      searchModule.setTime(result.time);
      this.searchLoading = false;
    } else {
      searchModule.setResults([]);
      searchModule.setTime(0);
    }
    const loginInfo: LoginInfo = commonModule.loginInfo;
    const favoriteIdList: ID[] = await fetchFavoriteMenuIdList(loginInfo);
    favoriteListModule.setUp(favoriteIdList);
  }

  private goBack(): void {
    router.go(-1);
  }

  private async executeSearch(text: string): Promise<void> {
    this.searchLoading = true;
    this.queryText = text;
    const result = await this.fetchResult(this.queryText);
    searchModule.setResults(result.resultList);
    searchModule.setTime(result.time);
    router.replace({
      name: 'searchQuery',
      params: { query: fixedEncodeURIComponent(text) },
    });
    this.searchLoading = false;
  }

  private executeEmptySearch(): void {
    this.queryText = '';
    searchModule.setResults([]);
    router.replace('/search');
  }

  private async fetchResult(text: string): Promise<SearchResult> {
    const start: number = Date.now();
    const loginInfo: LoginInfo = commonModule.loginInfo;
    const resultList: ProductMenu[] = await fetchSearchResult(
      {
        ...loginInfo,
        searchKey: text,
        language: loginUserModule.userInfo.language,
      },
      loginUserModule.productAdditionalQueryMap
    );
    const time: number = Date.now() - start;

    const tenantLandscape: TenantLandscape = commonModule.tenantLandscape;

    const isInHouseIPCWS: boolean = await isInHouseIP(
      tenantLandscape.tenant,
      tenantLandscape.landscape,
      Product.CWS
    );
    const isInHouseIPCSR: boolean = await isInHouseIP(
      tenantLandscape.tenant,
      tenantLandscape.landscape,
      Product.CSR
    );

    resultList.forEach((menu: ProductMenu) => {
      if (menu.product === Product.CWS) {
        if (!isInHouseIPCWS && !menu.canUseFromExtra) {
          menu.available = false;
        }
      } else if (menu.product === Product.CSR) {
        if (!isInHouseIPCSR && !menu.canUseFromExtra) {
          menu.available = false;
        }
      }
    });

    return { resultList, time };
  }

  private get searchResult(): ProductMenu[] {
    return searchModule.results;
  }

  private get searchTime(): number {
    return searchModule.time;
  }

  private get existQueryText(): boolean {
    return this.queryText.length > 0;
  }

  private async clickMenu(menu: ProductMenu): Promise<void> {
    const loginInfo: LoginInfo = commonModule.loginInfo;
    const { url, id, product } = menu;
    const param: UpdateTimeStampQueryParam = {
      ...loginInfo,
      menuId: id,
      product,
    };
    updateAccessedTimeStamp(param);
    window.open(url, getUrlTarget(product), REL);
  }

  private addFavorite(menu: ProductMenu): void {
    searchModule.addFavorite(menu.id);
    favoriteListModule.add(menu.id);
    this.updateFavorite(favoriteListModule.menuIdList);
  }

  private removeFavorite(id: ID): void {
    searchModule.removeFavorite(id);
    favoriteListModule.remove(id);
    this.updateFavorite(favoriteListModule.menuIdList);
  }

  private async updateFavorite(menuIds: ID[]): Promise<void> {
    const loginInfo: LoginInfo = commonModule.loginInfo;
    await updateFavoriteStatus({ ...loginInfo, menuIds });
  }

  private get userInfo(): UserInfo {
    return loginUserModule.userInfo;
  }

  private get loginInfo(): LoginInfo {
    return commonModule.loginInfo;
  }
}
